<template>
  <el-dialog :title="dialogTitle" :visible="visible" width="1200px" :close-on-click-modal="false" class="page-dialog center-dialog" @close="handleClose">
    <div class="dialog-content">
      <LanguageRadioButtons :value="currentLang" :options="langOptions" @input="handleLangInput" style="margin-bottom: 10px" />
      <el-form ref="formRef" :model="localFormData" :rules="formRules" label-width="120px" size="small">
        <el-form-item label="问题标题" prop="title">
          <el-input
            v-for="opt in langOptions"
            :key="opt.value"
            v-show="opt.value === currentLang"
            v-model="localFormData.title[opt.value]"
            placeholder="请输入问题标题"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="localFormData.sort" :min="0" :max="9999" controls-position="right" class="sort-input" />
        </el-form-item>
        <el-form-item label="所属问题分类" prop="type_id">
          <el-select v-model="localFormData.type_id" placeholder="请选择问题分类" style="width: 100%">
            <el-option v-for="item in categoryOptionsForLang" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="是否热门问题" prop="is_hot">
          <el-radio-group v-model="localFormData.is_hot">
            <el-radio :label="1">是</el-radio>
            <el-radio :label="2">否</el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 状态 -->
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="localFormData.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="2">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="问题内容" prop="content">
          <Tinymce
            v-for="opt in langOptions"
            v-if="visible"
            :key="`faq-q-content-${opt.value}-${tinymceKey}`"
            v-show="opt.value === currentLang"
            :value="localFormData.content[opt.value]"
            @input="val => (localFormData.content[opt.value] = val)"
            :height="260"
            language="zh"
          />
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">确认</el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import { addFaqQuestion, updateFaqQuestion } from '@/api/content/faq'
import Tinymce from '@/components/Tinymce'
import config from '@/config'
import LanguageRadioButtons from '@/components/LanguageRadioButtons'
import { computed, defineEmits, defineProps, getCurrentInstance, reactive, ref, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  dialogTitle: {
    type: String,
    default: '新增问题',
  },
  formData: {
    type: Object,
    default: () => ({}),
  },
  categoryOptions: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:visible', 'success'])

const { proxy } = getCurrentInstance()
const formRef = ref(null)
const loading = ref(false)
const tinymceKey = ref(0)

const langOptions = Array.isArray(config.languages) ? config.languages : []
const defaultLang = langOptions[0]?.value || 'EN'
const currentLang = ref(defaultLang)

function buildLangObj(initialValue = '') {
  const obj = {}
  for (let i = 0; i < langOptions.length; i += 1) {
    const k = langOptions[i]?.value
    if (k != null) obj[k] = initialValue
  }
  return obj
}

const localFormData = reactive({
  id: null,
  title: buildLangObj(''),
  sort: 0,
  type_id: null,
  is_hot: 0,
  content: buildLangObj(''),
  status: 1,
})

const formRules = {
  title: [
    {
      validator: (rule, value, callback) => {
        const t = localFormData.title?.[currentLang.value]
        if (!t) {
          callback(new Error('请输入问题标题'))
          return
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
  type_id: [{ required: true, message: '请选择所属问题分类', trigger: 'change' }],
  content: [
    {
      validator: (rule, value, callback) => {
        const c = localFormData.content?.[currentLang.value]
        if (!c) {
          callback(new Error('请输入问题内容'))
          return
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
  sort: [{ required: true, message: '请输入排序', trigger: 'blur' }],
}

const categoryOptionsForLang = computed(() => {
  const current = currentLang.value
  const fallback = defaultLang
  return (props.categoryOptions || []).map(opt => {
    const typeName = opt?.type_name
    let label = opt?.label
    if (typeName && typeof typeName === 'object' && !Array.isArray(typeName)) {
      label = typeName?.[current] || typeName?.[fallback] || ''
    }
    return { label, value: opt.value }
  })
})

watch(
  () => [props.visible, props.formData],
  () => {
    if (props.visible) {
      currentLang.value = defaultLang
      Object.assign(localFormData, {
        id: props.formData.id ?? null,
        title: (() => {
          const empty = buildLangObj('')
          const raw = props.formData.title
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) return { ...empty, ...raw }
          empty[currentLang.value] = raw || ''
          return empty
        })(),
        sort: props.formData.sort ?? 0,
        type_id: props.formData.type_id ?? null,
        is_hot: props.formData.is_hot !== undefined && props.formData.is_hot !== null ? props.formData.is_hot : 1,
        content: (() => {
          const empty = buildLangObj('')
          const raw = props.formData.content
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) return { ...empty, ...raw }
          empty[currentLang.value] = raw || ''
          return empty
        })(),
        status: props.formData.status !== undefined && props.formData.status !== null ? props.formData.status : 1,
      })
      tinymceKey.value += 1
    }
  },
  { immediate: true }
)

function handleClose() {
  emit('update:visible', false)
}

function handleLangInput(val) {
  currentLang.value = val
}

function handleConfirm() {
  formRef.value.validate(valid => {
    if (!valid) return
    loading.value = true
    const api = localFormData.id ? updateFaqQuestion : addFaqQuestion
    const payload = {
      title: localFormData.title,
      sort: localFormData.sort,
      type_id: localFormData.type_id,
      is_hot: localFormData.is_hot,
      content: localFormData.content,
      status: localFormData.status,
    }
    if (localFormData.id) payload.id = localFormData.id
    api(payload)
      .then(res => {
        if (res && res.code !== undefined && res.code !== 0) {
          throw new Error(res.message || '保存失败')
        }
        proxy.$message.success('保存成功')
        emit('success')
        handleClose()
      })
      .catch(error => {
        console.error('保存问题失败', error)
        proxy.$message.error(error?.response?.data?.message || error.message || '保存问题失败')
      })
      .finally(() => {
        loading.value = false
      })
  })
}
</script>

<style lang="scss" scoped>
.dialog-fixed-height {
  display: flex;
  align-items: center;
  justify-content: center;
}
.dialog-fixed-height ::v-deep .el-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  margin: auto;
  margin-top: 5vh !important;
}
.dialog-fixed-height ::v-deep .el-dialog__header {
  flex-shrink: 0;
}
.dialog-fixed-height ::v-deep .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.dialog-fixed-height ::v-deep .el-dialog__footer {
  flex-shrink: 0;
}
.sort-input {
  width: 120px;
}
::v-deep .sort-input.el-input-number {
  width: 120px;
}
</style>
